import { useState, useEffect, useCallback, useMemo } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';

import { useShowBenefitsModalV2 } from '../use-show-benefits-modal-v2/index.tsx';
import type { UseBenefitsModalReturnValue } from './types.tsx';

const JIRA_NAV4_BENEFITS_MODAL_KEY = 'jiraNav4BenefitsModal';
const MODAL_DELAY = 171_900_000; // 47.75 hours

// exposed for DI
export function getCurrentTime() {
	return Date.now();
}

export function useBenefitsModal(): UseBenefitsModalReturnValue {
	const [isModalShown, setModalShown] = useState(false);
	const storage = createLocalStorageProvider('jira-nav4-benefits-modal');
	const lastShown = Number(storage.get(JIRA_NAV4_BENEFITS_MODAL_KEY) || 0);
	const { isEligible } = useShowBenefitsModalV2();

	const dismissed = useMemo(() => {
		return storage.get(`${JIRA_NAV4_BENEFITS_MODAL_KEY}_dismissed`) ?? false;
	}, [storage]);

	const onDismiss = useCallback(() => {
		storage.set(`${JIRA_NAV4_BENEFITS_MODAL_KEY}_dismissed`, true);
	}, [storage]);

	const shouldShowModal = useCallback(() => {
		if (dismissed) return false;

		const isModalDelayFinished = lastShown + MODAL_DELAY < getCurrentTime();
		return isEligible && isModalDelayFinished;
	}, [dismissed, lastShown, isEligible]);

	useEffect(() => {
		if (isModalShown) {
			// Update the local storage
			storage.set(JIRA_NAV4_BENEFITS_MODAL_KEY, Date.now());
		}
	}, [isModalShown, storage]);

	return { shouldShowModal, setModalShown, onDismiss };
}
